<template>
  <div class="b-currency-rate-parameters-widget mt-4">
    <div class="row b-form__row_type_flex">
      <div class="col-3">
        <label>{{ $t('CONTENT.currency.currencyRate_type') }}:<span class="b-form__required"> *</span></label>
        <cool-select
          :list="allCurrencyCodeTypes"
          option-value="value"
          option-text="name"
          v-model="currencyCodeType"
          :placeholder="$t('CONTENT.choose')"
          :isError="isFieldWithError('currencyCodeType', currencyCodeType)"
        ></cool-select>
      </div>
      <div class="col-3" v-if="currentCurrencyCode === 'Imported'" :key="'Imported'">
        <label>{{ $t('CONTENT.currency.currencyRate_imported_name') }}:<span class="b-form__required"> *</span></label>
        <cool-select
          :list="importedCourseNames"
          option-value="value"
          option-text="name"
          v-model="currentImportedId"
          :placeholder="$t('CONTENT.choose')"
          :isError="isFieldWithError('currentImportedId', currentImportedId)"
        ></cool-select>
      </div>
      <div class="col-4"
           v-if="currentCurrencyCode === 'CBR' || (currentCurrencyCode === 'Imported' && currentImportedId)"
           :key="'CBR'">
        <label>{{ $t('CONTENT.currency.currencyRate_course') }}:</label>
        <div>{{ currentRateResult }}</div>
      </div>
      <div class="col-9" v-if="currentCurrencyCode === 'Corridor' || currentCurrencyCode === 'Corridor_Imported'"
           :key="'Corridor'">
        <div class="row b-form__row_type_flex">
          <div class="col-4">
            <label>min:</label>
            <numeric
              class="b-numeric"
              controls
              :min="minValueForCurrencyRateMin"
              :max="maxValueForCurrencyRateMin"
              v-model="currencyRateMin"
              @change="onChangeCurrencyRateCorridorMin"
            ></numeric>
          </div>
          <div class="col-4">
            <label>max:</label>
            <numeric
              class="b-numeric"
              controls
              :min="minValueForCurrencyRateMax"
              :max="maxValueForCurrencyRateMax"
              v-model="currencyRateMax"
              @change="onChangeCurrencyRateCorridorMax"
            ></numeric>
          </div>
          <div class="col-4">
            <label>{{ $t('CONTENT.currency.currencyRate_course') }}:</label>
            <div>{{ currentRateResult }}</div>
          </div>
        </div>
      </div>
      <div class="col-3" v-if="currentCurrencyCode === 'Fixed'" :key="'Fixed'">
        <label>{{ $t('CONTENT.currency.currencyRate_course') }}:</label>
        <numeric
          class="b-numeric"
          controls
          :min="0"
          :max="1000000000000"
          v-model="currencyRateFixedValue"
        ></numeric>
      </div>
    </div>
    <PercentAndPeriodRateWidget
      v-if="currencyCodeType === 'CBR'"
      :payment="payment"
    ></PercentAndPeriodRateWidget>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import dateConverter from "../../../utils/converters/date.converter";
import PercentAndPeriodRateWidget from './PercentAndPeriodRateWidget';

export default {
  components: {
    PercentAndPeriodRateWidget,
  },
  props: {
    lessorComponentSharesIndex: {
      type: Number,
      required: true,
    },
    payment: {
      type: Object
    },
    assetComponent: {
      type: Object,
      required: true
    },
    currencyCode: {
      type: String
    },
    paymentIndex: {
      type: Number
    },
    assetComponentIndex: {
      type: Number
    },
    importedCourseNames: {
      type: Array,
    },
    validationErrors: {
      type: Array
    }
  },
  data() {
    return {
      currentCurrencyCode: null,
      currentRateResult: null,
      minValueForCurrencyRateMin: 0,
      maxValueForCurrencyRateMin: 10000,
      minValueForCurrencyRateMax: 0,
      maxValueForCurrencyRateMax: 10000,
      allCurrencyCodeTypes: [
        {name: this.$t("CONTENT.currency.currencyRate_CBR"), value: "CBR"},
        {name: this.$t("CONTENT.currency.currencyRate_corridor"), value: "Corridor"},
        {name: this.$t("CONTENT.currency.currencyRate_fix"), value: "Fixed"},
        {name: this.$t("CONTENT.currency.currencyRate_imported"), value: "Imported"},
        { name: this.$t("CONTENT.currency.currencyRate_corridor_imported"), value: "Corridor_Imported" }
      ],
    };
  },
  watch: {
    currencyCode() {
      this.currencyCodeType = 'CBR';
    },
    currentCurrencyCode(val) {
      if (val === "CBR" || (val !== "Corridor" && val !== "Fixed" && val !== "Corridor_Imported" && val)) {
        this.getCurrentRate();
      }
      if (
        (val === "Corridor" || val === "Corridor_Imported") &&
        this.currencyRateMin !== null &&
        this.currencyRateMax !== null
      ) {
        this.getCurrentRate();
      }
      if (val === "Fixed" && this.currencyRateFixedValue !== null) {
        this.getCurrentRate();
      }
    },
    currentAssetComponentStartDate() {
      if (this.currentCurrencyCode === "CBR" || (this.currentCurrencyCode !== 'Corridor'
        && this.currentCurrencyCode !== 'Fixed' && this.currentCurrencyCode !== 'Corridor_Imported' && this.currentCurrencyCode)) {
        this.getCurrentRate();
      }
      if (
        (this.currentCurrencyCode === "Corridor" || this.currentCurrencyCode === "Corridor_Imported") &&
        this.currencyRateMin !== null &&
        this.currencyRateMax !== null
      ) {
        this.getCurrentRate();
      }
    },
    currencyRateMin(val, oldVal) {
      if (oldVal !== val) {
        this.getCurrentRate();
      }
    },
    currencyRateMax(val, oldVal) {
      if (oldVal !== val) {
        this.getCurrentRate();
      }
    },
    currencyRateFixedValue(val, oldVal) {
      if (oldVal !== val) {
        this.getCurrentRate();
      }
    }
  },
  computed: {
    ...mapGetters(["getCurrentContract"]),
    ...mapGetters(["getContractSettings"]),
    currentAssetComponentStartDate() {
      return this.assetComponent.startDate;
    },
    currencyRateFixedValue: {
      get() {
        return this.payment.currencyRateParameters.fixedValue;
      },
      set(newVal) {
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "fixedValue",
            value: newVal,
            payment: this.payment,
            lessorComponentSharesIndex: this.lessorComponentSharesIndex,
          }
        );
      }
    },
    currencyCodeType: {
      get() {
        return this.payment.currencyRateParameters.type;
      },
      set(newVal) {
        this.currentCurrencyCode = newVal;
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "type",
            value: newVal,
            payment: this.payment,
            lessorComponentSharesIndex: this.lessorComponentSharesIndex,
          }
        );
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "minValue",
            value: null,
            payment: this.payment,
            lessorComponentSharesIndex: this.lessorComponentSharesIndex,
          }
        );
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "maxValue",
            value: null,
            payment: this.payment,
            lessorComponentSharesIndex: this.lessorComponentSharesIndex,
          }
        );
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "fixedValue",
            value: null,
            payment: this.payment,
            lessorComponentSharesIndex: this.lessorComponentSharesIndex,
          }
        );
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "importedId",
            value: null,
            payment: this.payment,
            lessorComponentSharesIndex: this.lessorComponentSharesIndex,
          }
        );
        this.getCurrentRate();
      }
    },
    currentImportedId: {
      get() {
        return this.payment.currencyRateParameters.importedId;
      },
      set(newVal) {
        this.$store.commit(
          "assetComponentsPlugin/updatePaymentCurrencyRateParams",
          {
            name: "importedId",
            value: newVal,
            payment: this.payment
          }
        );
        this.getCurrentRate();
      }
    },
    currencyRateMin: {
      get() {
        return this.payment.currencyRateParameters.minValue;
      },
      set() {
      }
    },
    currencyRateMax: {
      get() {
        return this.payment.currencyRateParameters.maxValue;
      },
      set() {
      }
    }
  },
  created() {
    this.currencyCodeType = 'CBR';
  },
  methods: {
    onChangeCurrencyRateCorridorMin(newVal) {
      this.currencyRateMin = newVal;
      this.minValueForCurrencyRateMax = newVal + 1;
      this.$store.commit(
        "assetComponentsPlugin/updatePaymentCurrencyRateParams",
        {
          name: "minValue",
          value: newVal,
          payment: this.payment,
          lessorComponentSharesIndex: this.lessorComponentSharesIndex,
        }
      );
    },
    onChangeCurrencyRateCorridorMax(newVal) {
      this.currencyRateMax = newVal;
      this.maxValueForCurrencyRateMin = newVal - 1;
      this.$store.commit(
        "assetComponentsPlugin/updatePaymentCurrencyRateParams",
        {
          name: "maxValue",
          value: newVal,
          payment: this.payment,
          lessorComponentSharesIndex: this.lessorComponentSharesIndex,
        }
      );
    },
    async getCurrentRate() {
      let currentRate = null;
      let currencyRateData = {};
      let encodedStartDate = encodeURIComponent(
        this.currentAssetComponentStartDate
      );
      this.currentRateResult = null;

      currencyRateData.onDate = encodedStartDate;
      currencyRateData.type = this.currentCurrencyCode;
      currencyRateData.currencyCode = this.currencyCode;
      currencyRateData.minValue = this.currencyRateMin;
      currencyRateData.maxValue = this.currencyRateMax;
      currencyRateData.fixedValue = this.currencyRateFixedValue;

      if (
        this.currentAssetComponentStartDate === null ||
        this.currencyCode === null ||
        (this.currentCurrencyCode === "Corridor" &&
          (this.currencyRateMin === null || this.currencyRateMax === null))
      ) {
        this.currentRateResult = `${this.$t('CONTENT.fill_all_fields')}`;
        return;
      }

      if (
        this.currentCurrencyCode === "Fixed" &&
        this.currencyRateFixedValue === null
      ) {
        return;
      }

      if (
        this.currentCurrencyCode === 'Imported' && !this.currentImportedId) {
        return;
      }

      if (
        this.currentCurrencyCode === 'Imported' && this.currentImportedId) {
        currencyRateData.importedId = this.currentImportedId;
      }

      try {
        currentRate = await this.$store.dispatch(
          "getCurrencyRate",
          currencyRateData
        );
        this.setCurrentRateResult(currentRate);
      } catch (e) {
        this.currentRateResult = `${this.$t('CONTENT.no_data')}`;
      }
    },
    setCurrentRateResult(currentRate) {
      const dateObject = dateConverter.convertToDate(
        this.currentAssetComponentStartDate
      );
      this.currentRateResult = `${currentRate.value} (${
        currentRate.currencyCode
      }) на ${this.$d(dateObject)}`;
    },
    isFieldWithError(field, value) {
      if (!this.validationErrors || !this.validationErrors[this.payment.validationIndex]) {
        return;
      }
      return !value && this.validationErrors && this.validationErrors[this.payment.validationIndex].includes(field);
    },
  }
};
</script>
